// Split an owner's balance into OLD (legacy profile credit) and NEW (wallet credit) parts.
//
// The /me payload exposes `legacyCredit` (User.credit profile field), `walletBalance`
// (CreditWallet paid balance), `bonusCredit` (CreditWallet promo pool — signup + profile
// completion bonuses), `migrated`, and `credit` (the combined total of all three).
// While the owner is still un-migrated AND holds profile credit, the UI shows BOTH balances
// ("পুরনো 511 • ক্রেডিট 205"). Once migrated (or the profile field hits 0) only the single
// wallet balance is shown.
//
// `wallet` here is the TOTAL wallet-side credit the user can spend and should see:
// paid balance + promotional bonus (bonus is spent first server-side, but to the user it's
// one number). `paidWallet` and `bonus` are exposed separately for any breakdown UI.
export function creditParts(user) {
    const bonus = user?.bonusCredit ?? 0;
    const legacy = user?.legacyCredit ?? (user?.migrated ? 0 : (user?.credit ?? 0));
    // Paid wallet balance. Fallback (old payloads without walletBalance): for a migrated
    // owner the combined `credit` is wallet + bonus, so back out the bonus to isolate paid.
    const paidWallet = user?.walletBalance ?? (user?.migrated ? Math.max(0, (user?.credit ?? 0) - bonus) : 0);
    // Total wallet-side credit shown to the user = paid balance + promotional bonus.
    const wallet = paidWallet + bonus;
    // Legacy is shown while un-migrated AND the profile field is non-zero. A NEGATIVE
    // (advance/debt) balance must still show — only an exact 0 is hidden.
    const hasLegacy = !user?.migrated && legacy !== 0;
    // Wallet is shown only when it actually holds (or owes) credit — an owner with no
    // CreditWallet record or an exact 0 balance should NOT surface a "ক্রেডিট 0". A
    // negative wallet (advance usage) still shows. (Backend returns walletBalance: 0
    // for both "no row" and "row with 0", so 0 == nothing to show.)
    const hasWallet = wallet !== 0;
    // "Both" = the dual "old • new" display, only when BOTH parts are non-zero.
    const showBoth = hasLegacy && hasWallet;
    // Legacy-only: show the old balance alone (wallet is empty, so hide it).
    const showLegacyOnly = hasLegacy && !hasWallet;
    return { showBoth, showLegacyOnly, legacy, wallet, paidWallet, bonus, hasLegacy, hasWallet };
}
